import React, { useEffect, useState, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { useTable, usePagination } from 'react-table';
import * as XLSX from 'xlsx';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Select from 'react-select';
import Modal from 'react-modal';
import { AiOutlineClose, AiOutlineEdit, AiOutlineEye } from 'react-icons/ai';
import { IoIosCloseCircle } from "react-icons/io";
import SummaryApi from '../common';
import NotAnsweredModal from '../components/NotAnsweredModal';

const NotAnswered = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [studentsData, setStudentsData] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isUpload, setIsUpload] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [enrollmentCode, setEnrollmentCode] = useState(null);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [dropdownValue, setDropdownValue] = useState('');
  const [minEnrolledDate, setMinEnrolledDate] = useState(null);
  const [maxEnrolledDate, setMaxEnrolledDate] = useState(new Date());
  const [selectedRange, setSelectedRange] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [memberType, setMemberType] = useState(null);
  const [loading, setLoading] = useState(true);

  // search modal states
  const [searchModalIsOpen, setSearchModalIsOpen] = useState(false);
  const [searchDistrict, setSearchDistrict] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchCollege, setSearchCollege] = useState([]);
  const [isAdvancedSearchActive, setIsAdvancedSearchActive] = useState(false);

  // New states for highlighting and auto-scroll
  const [latestFollowUpId, setLatestFollowUpId] = useState(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  const tableRef = useRef(null);

  const reasons = [
    "Wrong Number",
    "Not Reachable",
    "Number Not Working",
    "Call Declined",
    "Busy"
  ];

  const districts = [
    'Alluri Sitharama Raju',
    'Anakapalli',
    'Ananthapuram',
    'Annamayya',
    'Bapatla',
    'CHITTOOR',
    'EAST GODAVARI',
    'Eluru',
    'Guntur',
    'Kakinada',
    'Konaseema',
    'Krishna',
    'Kurnool',
    'Manyam',
    'Nandyal',
    'NTR',
    'Palnadu',
    'Prakasam',
    'Srikakulam',
    'SPS Nellore',
    'Sri Satyasai',
    'Srikakulam',
    'Tirupathi',
    'Vizianagaram',
    'Visakhapatnam',
    'West Godavari',
    'YSR Kadapa'
  ];

  const headers = {
    'Authorization': 'Bearer sandeep_university_916',
    'Content-Type': 'application/json',
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const encodedMemberType = sessionStorage.getItem('K3M8X1JQ5VZ4B7PWTL9');
      const memberType = encodedMemberType ? atob(encodedMemberType) : null;
      setMemberType(memberType);
      let response;
      if (memberType === 'Admin' || memberType === 'Manager') {
        response = await fetch(SummaryApi.getAllnotansweredReport.url, {
          method: SummaryApi.getAllnotansweredReport.method,
          headers: {
            'Authorization': 'Bearer sandeep_university_916',
            'Content-Type': 'application/json',
          },
        });
      } else {
        const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
        if (!encodedEmployeeId) {
          throw new Error('Employee ID not found in session storage');
        }
        const decodedEmployeeId = atob(encodedEmployeeId);
        response = await fetch(SummaryApi.notansweredReport.url, {
          method: SummaryApi.notansweredReport.method,
          headers: {
            'Authorization': 'Bearer sandeep_university_916',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ employee_id: decodedEmployeeId }),
        });
      }
      if (!response.ok) {
        throw new Error('Failed to fetch data from the endpoint');
      }
      const data = await response.json();
      const students = data?.data || [];
      const updatedStudentsData = students.map((student, index) => ({
        ...student,
        sno: index + 1,
      }));

      setStudentsData(updatedStudentsData || []);
      
      // Find latest follow-up from the new data
      findLatestFollowUp(updatedStudentsData);
      
      // Apply current filters to the new data
      let filtered = applyRegularFilters(updatedStudentsData, selectedFilter, searchQuery, selectedRange);
      
      // If advanced search is active, apply those filters too
      if (isAdvancedSearchActive) {
        filtered = applyAdvancedFilters(filtered);
      }
      
      setFilteredStudents(filtered);
      findLatestFollowUp(filtered);
      setShouldAutoScroll(true);

    } catch (error) {
      toast.error(`Error fetching students: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to apply advanced filters
  const applyAdvancedFilters = (data) => {
    if (!isAdvancedSearchActive) return data;
    
    return data.filter(student => {
      const districtMatch = !searchDistrict ||
        student.district?.toLowerCase() === searchDistrict.toLowerCase();
      const cityMatch = !searchCity ||
        student.city?.toLowerCase() === searchCity.toLowerCase();
      const collegeMatch =
        searchCollege.length === 0 ||
        searchCollege.some(college =>
          student.clg_name?.toLowerCase() === college.toLowerCase()
        );
      return districtMatch && cityMatch && collegeMatch;
    });
  };

  // Function to find the student with the most recent follow-up date
  const findLatestFollowUp = (data) => {
    let latestIndex = -1;
    let latestDate = null;

    data.forEach((student, index) => {
      const fud = student.follow_up_date?.trim();
      if (!fud || fud === "0000-00-00 00:00:00" || fud === "N/A") return;
      const dateObj = new Date(fud);
      if (isNaN(dateObj.getTime())) return;
      if (!latestDate || dateObj > latestDate) {
        latestDate = dateObj;
        latestIndex = index;
      }
    });

    if (latestIndex !== -1) {
      setLatestFollowUpId(data[latestIndex].student_id);
    } else {
      setLatestFollowUpId(null);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (studentsData?.length) {
      const dates = studentsData.map(student => new Date(student.created_date));
      const minDate = new Date(Math.min(...dates));
      setMinEnrolledDate(minDate);
      setMaxEnrolledDate(new Date());
    }
  }, [studentsData]);

  // Apply regular filters function (pure function that returns filtered array)
  const applyRegularFilters = (data, filter, search, range) => {
    let filtered = [...data];
    const timeZone = 'Asia/Kolkata';

    const formatToKolkata = (date) => {
      return new Intl.DateTimeFormat('en-IN', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    };

    // Apply status filter
    if (filter !== 'All') {
      filtered = filtered.filter((student) => student.call_status === filter);
    }

    const convertToKolkataDateString = (date) => formatToKolkata(new Date(date));

    // Apply date range filter
    if (range === 'today') {
      const today = new Date();
      const todayFormatted = formatToKolkata(today);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        const studentDateFormatted = convertToKolkataDateString(studentDate);
        return studentDateFormatted === todayFormatted;
      });
    } else if (range === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayFormatted = formatToKolkata(yesterday);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        const studentDateFormatted = convertToKolkataDateString(studentDate);
        return studentDateFormatted === yesterdayFormatted;
      });
    } else if (range === 'thisWeek') {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= startOfWeek && studentDate <= endOfWeek;
      });
    } else if (range === 'lastWeek') {
      const today = new Date();
      const startOfCurrentWeek = new Date(today);
      startOfCurrentWeek.setDate(today.getDate() - today.getDay());
      startOfCurrentWeek.setHours(0, 0, 0, 0);
      const endOfLastWeek = new Date(startOfCurrentWeek);
      endOfLastWeek.setDate(startOfCurrentWeek.getDate() - 1);
      endOfLastWeek.setHours(23, 59, 59, 999);
      const startOfLastWeek = new Date(endOfLastWeek);
      startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
      startOfLastWeek.setHours(0, 0, 0, 0);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= startOfLastWeek && studentDate <= endOfLastWeek;
      });
    } else if (range === 'custom') {
      if (fromDate && toDate) {
        const fromFormatted = new Date(fromDate);
        fromFormatted.setHours(0, 0, 0, 0);
        const toFormatted = new Date(toDate);
        toFormatted.setHours(23, 59, 59, 999);
        filtered = filtered.filter((student) => {
          const studentDate = new Date(student.created_date);
          return studentDate >= fromFormatted && studentDate <= toFormatted;
        });
      }
    }

    // Apply search filter
    if (['Admin', 'Manager'].includes(memberType) && search) {
      filtered = filtered.filter((student) =>
        student.student_name?.toLowerCase().includes(search.toLowerCase()) ||
        student.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
        student.mobile_number1?.toLowerCase().includes(search.toLowerCase())
      );
    } else if (search) {
      filtered = filtered.filter((student) =>
        student.student_name?.toLowerCase().includes(search.toLowerCase()) ||
        student.mobile_number1?.toLowerCase().includes(search.toLowerCase())
      );
    }

    return filtered;
  };

  const filterStudents = (filter, search, range) => {
    let filtered = applyRegularFilters(studentsData, filter, search, range);
    
    // Apply advanced filters if active
    if (isAdvancedSearchActive) {
      filtered = applyAdvancedFilters(filtered);
    }
    
    setFilteredStudents(filtered);
    findLatestFollowUp(filtered);
  };

  const handleRangeChange = (e) => {
    const range = e.target.value;
    setSelectedRange(range);
    setIsCustomRange(range === 'custom');
    
    if (range !== 'custom') {
      setFromDate(null);
      setToDate(null);
      filterStudents(selectedFilter, searchQuery, range);
    } else {
      // For custom range, don't filter yet - wait for date selection
      setFilteredStudents([]);
    }
  };

  useEffect(() => {
    if (fromDate && toDate && isCustomRange) {
      filterStudents(selectedFilter, searchQuery, 'custom');
    }
  }, [fromDate, toDate]);

  const handleSearchChange = (e) => {
    const search = e.target.value;
    setSearchQuery(search);
    filterStudents(selectedFilter, search, selectedRange);
  };

  const openModal = (student = null, mode = 'view') => {
    setSelectedStudent(student);
    setIsUpdate(mode === 'edit');
    setIsUpload(mode === 'add');
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedStudent(null);
    setIsUpdate(false);
    setIsUpload(false);
  };

  // Function to handle "All" button click
  const handleAllClick = () => {
    setSelectedFilter('All');
    let filtered = applyRegularFilters(studentsData, 'All', searchQuery, selectedRange);
    
    // Apply advanced filters if active
    if (isAdvancedSearchActive) {
      filtered = applyAdvancedFilters(filtered);
    }
    
    setFilteredStudents(filtered);
    findLatestFollowUp(filtered);
    setShouldAutoScroll(true);
  };

  const downloadExcelReport = () => {
    const requiredFields = [
      'enrollment_code',
      'student_name',
      'email',
      'mobile_number1',
      'mobile_number2',
      'aadhar_number',
      'state',
      'district',
      'city',
      'address',
      'pincode',
      'course',
      'clg_name',
      'created_date',
      'call_status',
      'call_duration',
      'remarks',
      'employee_name'
    ];
    const filteredData = studentsData.map(student => {
      const selectedData = {};
      requiredFields.forEach(field => {
        selectedData[field] = student[field];
      });
      return selectedData;
    });

    const headers = {
      enrollment_code: 'Enrollment Code',
      student_name: 'Student Name',
      email: 'Email',
      mobile_number1: 'Mobile Number 1',
      mobile_number2: 'Mobile Number 2',
      aadhar_number: 'Aadhar Number',
      state: 'State',
      district: 'District',
      city: 'City',
      address: 'Address',
      pincode: 'Pincode',
      course: 'Course',
      clg_name: 'College',
      created_date: 'Enrolled Date',
      call_status: 'Call Status',
      call_duration: 'Call Duration',
      remarks: 'Remarks',
      employee_name: 'Employee Name'
    };

    const formattedData = filteredData.map(student => {
      return Object.fromEntries(
        Object.entries(student).map(([key, value]) => [headers[key], value])
      );
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');
    XLSX.writeFile(workbook, 'students_report.xlsx');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return "NA";
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  const formatDateForComparison = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  // Function to get row class based on follow-up date
  const getRowClass = (student) => {
    let base = "transition duration-200";

    const fud = student.follow_up_date ? new Date(student.follow_up_date) : null;
    const isPast = fud && fud.getTime() <= new Date().getTime();
    const isLatest = student.student_id === latestFollowUpId;

    if (isLatest) {
      base += " bg-yellow-200 border-4 border-yellow-600 shadow-xl font-semibold";
    } 
    else if (isPast) {
      base += " bg-green-100 border-2 border-green-500 font-semibold";
    } 
    else {
      base += " hover:bg-gray-100";
    }

    return base;
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'SNO',
        Cell: ({ row }) => row.index + 1,
      },
      ...(['Admin', 'Manager'].includes(memberType) ? [
        {
          Header: 'Employee Name',
          accessor: 'employee_name',
        },
      ] : []),
      {
        Header: 'Student Name',
        accessor: 'student_name',
      },
      {
        Header: 'District',
        accessor: 'district',
      },
      {
        Header: 'College Name',
        accessor: 'clg_name',
      },
      {
        Header: 'Address',
        accessor: 'address',
      },
      {
        Header: 'Mobile Number',
        accessor: 'mobile_number1',
      },
      {
        Header: 'Call Duration (HH:MM:SS)',
        accessor: 'call_duration',
        Cell: ({ value }) => {
          if (value === "n/a") return value;
          if (value.includes(':')) {
            const [timePart] = value.split('.');
            const [hours, minutes, seconds] = timePart.split(':');
            return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
          }
          return value;
        },
      },
      {
        Header: 'Course',
        accessor: 'course',
      },
      {
        Header: 'Enrolled Date',
        accessor: 'enroll_date',
        Cell: ({ row }) => {
          const { created_date } = row.original;
          return <span>{formatDate(created_date)}</span>;
        },
      },
      {
        Header: 'Follow up Date',
        accessor: 'followup_date',
        Cell: ({ row }) => {
          const { follow_up_date } = row.original;
          return <span>{formatDate(follow_up_date)}</span>;
        },
      },
      {
        Header: 'Status',
        accessor: 'is_interested',
        Cell: ({ value }) => {
          let colorClass = 'text-gray-600';
          if (value === 'Most Interested') colorClass = 'text-green-600';
          else if (value === 'Interested') colorClass = 'text-yellow-500';
          else if (value === 'Not Interested') colorClass = 'text-red-500';
          return (
            <span className={`font-semibold ${colorClass}`}>
              {value}
            </span>
          );
        },
      },
      ...(['Employee', 'Consultant'].includes(memberType) ? [{
        Header: 'Edit',
        accessor: 'edit',
        Cell: ({ row }) => (
          <button
            className={`bg-green-600 text-white px-3 py-2 rounded-md transition-transform transform hover:scale-105`}
            onClick={() => openModal(row.original, 'edit')}
          >
            <AiOutlineEdit className="inline-block mr-1" />
            Edit
          </button>
        ),
      }] : []),
      {
        Header: 'View More',
        accessor: 'view',
        Cell: ({ row }) => (
          <button
            className="bg-yellow-600 text-white px-3 py-2 rounded-md transition-transform transform hover:scale-105"
            onClick={() => openModal(row.original, 'view')}
          >
            <AiOutlineEye className="inline-block mr-1" />
            View More
          </button>
        ),
      },
    ],
    [memberType, latestFollowUpId]
  );

  const reversedData = useMemo(() => {
    return [...filteredStudents].reverse();
  }, [filteredStudents]);

  const tableInstance = useTable(
    {
      columns,
      data: reversedData,
      initialState: { pageIndex: 0 },
    },
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageCount,
  } = tableInstance;

  // Auto-scroll to latest follow-up
  useEffect(() => {
    if (!shouldAutoScroll || !latestFollowUpId || !tableRef.current || reversedData.length === 0) return;

    const scrollAndHighlightLatest = () => {
      const row = tableRef.current.querySelector(`tr[data-student-id="${latestFollowUpId}"]`);
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        setShouldAutoScroll(false);
      }
    };

    const studentIndex = reversedData.findIndex(s => s.student_id === latestFollowUpId);
    if (studentIndex === -1) {
      setShouldAutoScroll(false);
      return;
    }

    const targetPage = Math.floor(studentIndex / pageSize);

    if (pageIndex !== targetPage) {
      gotoPage(targetPage);
      setTimeout(scrollAndHighlightLatest, 600);
    } else {
      setTimeout(scrollAndHighlightLatest, 200);
    }
  }, [shouldAutoScroll, latestFollowUpId, reversedData, pageIndex, pageSize, gotoPage]);

  const collegeOptions = useMemo(() => {
    return Array.from(new Set(
      studentsData
        .filter(student =>
          searchCity && student.city?.toLowerCase() === searchCity.toLowerCase()
        )
        .map(student => student.clg_name)
    ))
      .filter(Boolean)
      .map(college => ({
        value: college,
        label: college
      }));
  }, [studentsData, searchCity]);

  const clearFilters = () => {
    setSelectedRange('');
    setFromDate(null);
    setToDate(null);
    setIsCustomRange(false);
    setSearchQuery('');
    setSelectedFilter('All');
    setSearchDistrict('');
    setSearchCity('');
    setSearchCollege([]);
    setIsAdvancedSearchActive(false);
    
    const filtered = applyRegularFilters(studentsData, 'All', '', '');
    setFilteredStudents(filtered);
    findLatestFollowUp(filtered);
    setShouldAutoScroll(true);
  };

  return (
    <div className="mx-auto">
      {loading ? (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-20 flex justify-center items-center">
          <span className="text-white text-lg">Fetching Data...</span>
        </div>
      ) : (
        <>
          <div className="mx-auto">
            {loadingSubmit && (
              <div className="fixed inset-0 bg-black bg-opacity-70 z-20 flex justify-center items-center">
                <span className="text-white text-lg">Submitting...</span>
              </div>
            )}

            <div className='flex justify-between mb-6'>
              <h1 className="text-3xl font-bold text-center">Not Answered Students</h1>
              <div>
                <button
                  onClick={() => {
                    setSearchModalIsOpen(true);
                  }}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg ml-4 flex items-center"
                >
                  Advanced Search
                </button>
              </div>
            </div>

            <div className='flex justify-between'>
              {['Admin', 'Manager'].includes(memberType) && (
                <div>
                  <label htmlFor="dateRange">Select Date Range:</label>
                  <select id="dateRange" className='bg-blue-500 py-2 px-4 text-white rounded-lg ml-4' onChange={handleRangeChange} value={selectedRange}>
                    <option value="">Select a range</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="thisWeek">This Week</option>
                    <option value="lastWeek">Last Week</option>
                    <option value="custom">Custom</option>
                  </select>
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 ml-4 bg-red-500 text-white rounded-lg hover:bg-red-600 focus:outline-none"
                  >
                    Clear Filters
                  </button>

                  {isCustomRange && (
                    <div className='flex mt-4 gap-4'>
                      <div>
                        <label>From:</label>
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => setFromDate(date)}
                          selectsStart
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={minEnrolledDate}
                          maxDate={toDate || maxEnrolledDate}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="Select From Date"
                          className='p-2 ml-2 border rounded-lg'
                        />
                      </div>
                      <div>
                        <label>To:</label>
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => setToDate(date)}
                          selectsEnd
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={fromDate || minEnrolledDate}
                          maxDate={maxEnrolledDate}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="Select To Date"
                          className='p-2 ml-2 border rounded-lg'
                          disabled={!fromDate}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className='w-1/4'>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={['Admin', 'Manager'].includes(memberType) ? "Search by student name or mobile number or employee name" : "Search by student name or mobile number"}
                  className="py-2 px-4 border rounded-lg w-full"
                />
              </div>
            </div>

            <div className="overflow-x-auto p-4">
              <div ref={tableRef}>
                <table {...getTableProps()} className="min-w-full mb-4 rounded-lg shadow-md overflow-hidden">
                  <thead className="bg-gray-200">
                    {headerGroups.map(headerGroup => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                          <th {...column.getHeaderProps()} className="px-4 py-2 text-left text-gray-700 font-semibold">
                            {column.render('Header')}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map(row => {
                      prepareRow(row);
                      const student = row.original;
                      const studentId = student.student_id;
                      const rowClass = getRowClass(student);

                      return (
                        <tr
                          {...row.getRowProps()}
                          data-student-id={studentId}
                          className={rowClass}
                        >
                          {row.cells.map(cell => (
                            <td {...cell.getCellProps()} className="border px-4 py-2">
                              {cell.render('Cell')}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center mb-4">
                <span>
                  Page {pageIndex + 1} of {pageCount} of {filteredStudents.length} entries
                </span>
                <div>
                  <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="px-3 py-1 border rounded">{"<<"}</button>
                  <button onClick={previousPage} disabled={!canPreviousPage} className="px-3 py-1 border rounded">{"<"}</button>
                  <button onClick={nextPage} disabled={!canNextPage} className="px-3 py-1 border rounded">{">"}</button>
                  <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} className="px-3 py-1 border rounded">{">>"}</button>
                </div>
                <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="ml-4 border rounded">
                  {[5, 10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Main Modal – contains NotAnsweredModal component */}
            <Modal
              isOpen={modalIsOpen}
              onRequestClose={closeModal}
              contentLabel="Student Details"
              className="mx-auto my-10 mt-[100px] max-h-[85vh] max-w-full sm:max-w-4xl bg-white rounded-lg shadow-lg overflow-auto"
              overlayClassName="fixed inset-0 bg-black bg-opacity-50"
            >
              <NotAnsweredModal
                isOpen={modalIsOpen}
                onClose={closeModal}
                student={selectedStudent}
                isUpdate={isUpdate}
                isUpload={isUpload}
                memberType={memberType}
                loadingSubmit={loadingSubmit}
                setLoadingSubmit={setLoadingSubmit}
                enrollmentCode={enrollmentCode}
                setEnrollmentCode={setEnrollmentCode}
                fetchStudents={fetchStudents}
                reasons={reasons}
                districts={districts}
              />
            </Modal>

            {/* Advanced Search Modal */}
            <Modal
              isOpen={searchModalIsOpen}
              contentLabel="Search Filters"
              className="mx-auto my-10 mt-[100px] max-w-md bg-white rounded-lg shadow-lg"
              overlayClassName="fixed inset-0 bg-black bg-opacity-50"
              onRequestClose={() => setSearchModalIsOpen(false)}
            >
              <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">Search Filters</h2>
                  <button onClick={() => setSearchModalIsOpen(false)}>
                    <AiOutlineClose className="w-6 h-6 text-gray-600 hover:text-gray-800" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex flex-col">
                  <label htmlFor="district-filter" className="mb-1 font-medium">District</label>
                  <select
                    id="district-filter"
                    className="p-2 border rounded"
                    value={searchDistrict}
                    onChange={(e) => {
                      setSearchDistrict(e.target.value);
                      setSearchCity('');
                      setSearchCollege([]);
                    }}
                  >
                    <option value="">Select District</option>
                    {Array.from(new Set(studentsData.map(student => student.district)))
                      .filter(Boolean)
                      .map(district => (
                        <option key={district} value={district}>{district}</option>
                      ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label htmlFor="city-filter" className="mb-1 font-medium">City</label>
                  <select
                    id="city-filter"
                    className="p-2 border rounded"
                    value={searchCity}
                    onChange={(e) => {
                      setSearchCity(e.target.value);
                      setSearchCollege([]);
                    }}
                    disabled={!searchDistrict}
                  >
                    <option value="">{searchDistrict ? "Select City" : "First select District"}</option>
                    {Array.from(new Set(
                      studentsData
                        .filter(student => searchDistrict && student.district?.toLowerCase() === searchDistrict.toLowerCase())
                        .map(student => student.city)
                    ))
                      .filter(Boolean)
                      .map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label htmlFor="college-filter" className="mb-1 font-medium">College Name</label>
                  <Select
                    isMulti
                    options={collegeOptions}
                    value={collegeOptions.filter(option => searchCollege.includes(option.value))}
                    onChange={(selectedOptions) =>
                      setSearchCollege(selectedOptions.map(option => option.value))
                    }
                    isDisabled={!searchCity}
                    placeholder={searchCity ? "Select College(s)" : "First select City"}
                  />
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => {
                      setSearchDistrict('');
                      setSearchCity('');
                      setSearchCollege([]);
                    }}
                    className="px-4 py-2 mr-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => {
                      // Apply advanced filters
                      setIsAdvancedSearchActive(true);
                      
                      // Filter the data
                      let filtered = applyRegularFilters(studentsData, selectedFilter, searchQuery, selectedRange);
                      
                      // Apply advanced filters
                      filtered = filtered.filter(student => {
                        const districtMatch = !searchDistrict ||
                          student.district?.toLowerCase() === searchDistrict.toLowerCase();
                        const cityMatch = !searchCity ||
                          student.city?.toLowerCase() === searchCity.toLowerCase();
                        const collegeMatch =
                          searchCollege.length === 0 ||
                          searchCollege.some(college =>
                            student.clg_name?.toLowerCase() === college.toLowerCase()
                          );
                        return districtMatch && cityMatch && collegeMatch;
                      });
                      
                      setFilteredStudents(filtered);
                      findLatestFollowUp(filtered);
                      setSearchModalIsOpen(false);
                      setShouldAutoScroll(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </Modal>
          </div>
        </>
      )}
    </div>
  );
};

export default NotAnswered;